import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import { api, ApiError } from '../core/api-client';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';

const CURRENCIES = ['USD', 'PKR', 'EUR', 'GBP', 'AED', 'INR'];
const GATEWAYS = [
  { value: 'manual',  label: 'Manual (no online payments)' },
  { value: 'stripe',  label: 'Stripe' },
  { value: 'safepay', label: 'SafePay' },
  { value: 'square',  label: 'Square' },
];
const MODULES = [
  { key: 'invoicing',  label: 'Invoicing & Clients' },
  { key: 'payments',   label: 'Payments' },
  { key: 'email',      label: 'Email' },
  { key: 'hr',         label: 'HR (Attendance + Leave)' },
  { key: 'payroll',    label: 'Payroll' },
  { key: 'monitoring', label: 'Employee Monitoring (Electron Agent)' },
  { key: 'projects',   label: 'Projects + Time Tracking' },
  { key: 'expenses',   label: 'Expenses' },
  { key: 'esign',      label: 'E-Sign' },
];

export function CreateTenantPage() {
  const navigate = useNavigate();
  const [form, setForm] = React.useState({
    name: '',
    slug: '',
    currency: 'USD',
    paymentGatewayChoice: 'manual',
    ownerEmail: '',
    ownerName: '',
  });
  const [enabledModules, setEnabledModules] = React.useState<string[]>(['invoicing']);
  const [submitting, setSubmitting] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [createdSummary, setCreatedSummary] = React.useState<string | null>(null);

  function update<K extends keyof typeof form>(key: K, val: typeof form[K]) {
    setForm((f) => ({ ...f, [key]: val }));
  }

  function autoSlug(name: string) {
    return name.toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
  }

  function toggleModule(key: string) {
    setEnabledModules((prev) => prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]);
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const result = await api<{ tenant: any; owner: any }>(
        '/api/admin/tenants',
        { method: 'POST', body: { ...form, enabledModules } },
      );
      setCreatedSummary(`Tenant "${result.tenant.name}" created. Magic link sent to ${result.owner.email}.`);
      setTimeout(() => navigate('/admin/tenants'), 2500);
    } catch (err) {
      if (err instanceof ApiError) setError(err.message);
      else setError(String((err as any)?.message ?? err));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="px-8 py-8 max-w-2xl mx-auto space-y-8">
      <div>
        <p className="text-[11px] uppercase tracking-[0.12em] text-muted-foreground mb-2">Platform</p>
        <h1 className="font-serif text-3xl tracking-tight text-foreground">Create tenant</h1>
        <p className="text-sm text-muted-foreground mt-1.5">
          Sets up a new company with an owner account. Owner gets a magic-link email to set their password.
        </p>
      </div>

      <Card>
        <CardHeader><CardTitle>Company</CardTitle></CardHeader>
        <CardContent>
          <form onSubmit={onSubmit} className="space-y-5">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="name">Company name</Label>
                <Input
                  id="name" required value={form.name}
                  onChange={(e) => { update('name', e.target.value); if (!form.slug) update('slug', autoSlug(e.target.value)); }}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="slug">URL slug</Label>
                <Input
                  id="slug" required value={form.slug}
                  pattern="[a-z0-9-]+" title="lowercase letters, numbers, and hyphens"
                  onChange={(e) => update('slug', e.target.value)}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="currency">Currency (locked at creation)</Label>
                <select
                  id="currency"
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
                  value={form.currency}
                  onChange={(e) => update('currency', e.target.value)}
                >
                  {CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="gateway">Payment gateway (locked at creation)</Label>
                <select
                  id="gateway"
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
                  value={form.paymentGatewayChoice}
                  onChange={(e) => update('paymentGatewayChoice', e.target.value)}
                >
                  {GATEWAYS.map((g) => <option key={g.value} value={g.value}>{g.label}</option>)}
                </select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="ownerName">Owner name</Label>
                <Input id="ownerName" required value={form.ownerName} onChange={(e) => update('ownerName', e.target.value)} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="ownerEmail">Owner email</Label>
                <Input id="ownerEmail" type="email" required value={form.ownerEmail} onChange={(e) => update('ownerEmail', e.target.value)} />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Enabled modules</Label>
              <div className="grid grid-cols-2 gap-2">
                {MODULES.map((m) => (
                  <label key={m.key} className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={enabledModules.includes(m.key)}
                      onChange={() => toggleModule(m.key)}
                    />
                    {m.label}
                  </label>
                ))}
              </div>
            </div>

            {error && <div className="text-sm text-destructive">{error}</div>}
            {createdSummary && <div className="text-sm text-green-600 dark:text-green-300">{createdSummary}</div>}

            <div className="flex gap-3">
              <Button type="submit" disabled={submitting}>
                {submitting ? 'Creating…' : 'Create tenant'}
              </Button>
              <Button type="button" variant="outline" onClick={() => navigate('/admin/tenants')}>
                Cancel
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
